




















































































































import { mapGetters, mapMutations, mapState } from "vuex";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import {
  Color,
  Guid,
  SpreadSheetLayerSetting,
  SpreadSheetLayerSettingsInterfaceRO,
} from "@wwtelescope/engine";
import { ApplyTableLayerSettingsOptions } from "@wwtelescope/engine-helpers";
import { PlotTypes } from "@wwtelescope/engine-types";

import { wwtEngineNamespace, wwtResearchAppNamespace } from "./namespaces";
import { CatalogLayerInfo, ImagesetInfo } from "@wwtelescope/engine-vuex";

interface UiPlotTypes {
  wwt: PlotTypes;
  desc: string;
}

const uiPlotTypes: UiPlotTypes[] = [
  { wwt: PlotTypes.gaussian, desc: "Gaussian" },
  { wwt: PlotTypes.circle, desc: "Circle" },
  { wwt: PlotTypes.pushPin, desc: "Push-pin" },
  // The other types don't currently render well.
  // "point": starts out OK but gets gnarly if the catalog is dense and you zoom in.
  // "square": actually renders as a flag
  // "custom": handled same as push-pin in the engine
];

interface VueColorData {
  rgba: {
    a: number;
    r: number;
    g: number;
    b: number;
  };
}

@Component
export default class CatalogItem extends Vue {
  @Prop({ required: true }) layer!: CatalogLayerInfo;
  @Prop({ required: false, default: Color.fromArgb(1, 255, 255, 255) })

  defaultColor!: Color;

  uiPlotTypes = uiPlotTypes;

  // Vuex integration

  beforeCreate(): void {
    this.$options.computed = {
      ...mapState(wwtResearchAppNamespace, {
        visible: (_state, getters) =>
          getters["researchAppTableLayerVisibility"](this.layer),
      }),
      ...mapGetters(wwtEngineNamespace, [
        "spreadsheetState",
      ]),
      ...this.$options.computed,
    };

    this.$options.methods = {
      ...this.$options.methods,
      ...mapMutations(wwtEngineNamespace, [
        "applyTableLayerSettings",
        "deleteLayer",
        "removeCatalogHipsByName",
      ]),
      ...mapMutations(wwtResearchAppNamespace, [
        "removeResearchAppTableLayer",
        "setResearchAppTableLayerVisibility",
      ]),
    };
  }

  // Tied to the store value
  visible!: boolean;

  spreadsheetState!: (layer: CatalogLayerInfo) => SpreadSheetLayerSettingsInterfaceRO | null;

  applyTableLayerSettings!: (_o: ApplyTableLayerSettingsOptions) => void;
  deleteLayer!: (id: string | Guid) => void;
  removeCatalogHipsByName!: (name: string) => void;
  removeResearchAppTableLayer!: (layer: CatalogLayerInfo) => void;
  setResearchAppTableLayerVisibility!: (args: {
    layer: CatalogLayerInfo;
    visible: boolean;
  }) => void;

  // Local state

  editing = false;
  hasFocus = false;
  isSelected = false;
  isLayerHips = false;

  layerId(): string {
    return this.layer.id ?? "";
  }

  layerState(): SpreadSheetLayerSettingsInterfaceRO | null {
    return this.spreadsheetState(this.layer);
  }

  get color(): Color {
    const state = this.layerState();

    if (state !== null) {
      return state.get_color();
    } else {
      return this.defaultColor;
    }
  }

  set color(value: Color) {
    this.applySettings([
      ["color", value],
      ["opacity", value.a],
    ]);
  }

  get enabled(): boolean {
    const state = this.layerState();

    if (state !== null) {
      return state.get_enabled();
    } else {
      return true;
    }
  }

  get plotType(): PlotTypes {
    const state = this.layerState();

    if (state !== null) {
      return state.get_plotType();
    }

    return PlotTypes.gaussian;
  }

  set plotType(value: PlotTypes) {
    this.applySettings([["plotType", value]]);
  }

  get scaleFactorDb(): number {
    const state = this.layerState();

    if (state !== null) {
      return 10 * Math.log10(state.get_scaleFactor());
    }

    return 0.0;
  }

  set scaleFactorDb(value: number) {
    this.applySettings([["scaleFactor", Math.pow(10, 0.1 * value)]]);
  }

  // I can't find a customizable Vue numeric input that has good TypeScript
  // support, so we just hand-roll the processing to give nice textual
  // presentation.
  get scaleFactorDbText(): string {
    if (this.scaleFactorDb == 0) {
      return "0.00";
    } else if (this.scaleFactorDb < 0) {
      return this.scaleFactorDb.toFixed(2);
    } else {
      return "+" + this.scaleFactorDb.toFixed(2);
    }
  }

  set scaleFactorDbText(value: string) {
    const n = Number(value);

    if (isFinite(n)) {
      this.scaleFactorDb = n;
    }
  }

  // Implementation

  mounted() {
    this.isLayerHips = this.layer instanceof ImagesetInfo;
  }

  private applySettings(settings: SpreadSheetLayerSetting[]) {
    this.applyTableLayerSettings({
      id: this.layerId(),
      settings: settings,
    });
  }

  handleDelete() {
    this.removeResearchAppTableLayer(this.layer);
    if (this.layer instanceof ImagesetInfo) {
      this.removeCatalogHipsByName(this.layer.name);
    } else {
      this.deleteLayer(this.layer.id);
    }
  }

  handleToggle() {
    this.setResearchAppTableLayerVisibility({
      layer: this.layer,
      visible: !this.visible,
    });
  }

  handleColorChange(colorData: VueColorData) {
    const rgba = colorData["rgba"];
    this.color = Color.fromArgb(rgba["a"], rgba["r"], rgba["g"], rgba["b"]);
  }

  handleEditClick() {
    this.editing = !this.editing;
  }

  @Watch("visible")
  onVisibilityChange(val: boolean) {
    this.applySettings([["enabled", val]]);
  }

  @Watch("enabled")
  onEnabledChange(val: boolean) {
    this.setResearchAppTableLayerVisibility({
      layer: this.layer,
      visible: val,
    });
  }

  @Watch("editing")
  editingChanged(val: boolean, _oldVal: boolean) {
    const input: HTMLInputElement = this.$el.getElementsByClassName(
      "name-input"
    )[0] as HTMLInputElement;
    if (val) {
      input.value = this.layer.name;
    } else {
      this.layer.name = input.value;
    }
  }

  doAdjustSize(bigger: boolean) {
    if (bigger) {
      this.scaleFactorDb += 0.5;
    } else {
      this.scaleFactorDb -= 0.5;
    }
  }
}
